import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { LabeledToggleSwitch } from '@/components/ui/LabeledToggleSwitch';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import { detailsShellClass, detailsSummaryClass, detailsTitleClass } from '@/components/ui/cardStyles';
import { SegmentedTabs } from '@/components/ui/SegmentedTabs';
import {
  MicroPromotionDefaultMomentsImages,
  type DefaultMomentsImageItem,
} from '@/components/MicroPromotionDefaultMomentsImages';
import {
  parseTimeRangesFromApi,
  validateWeworkPushSchedule,
  WeworkPushTimeRangesEditor,
  type WeworkPushTimeRangeRow,
} from '@/components/WeworkPushTimeRangesEditor';

export type MicroPromoInfoType = 'graphic' | 'recruitment';

const GRAPHIC_MAP_LABELS: Record<string, string> = {
  promotion_text: '推广信息',
  contact_phone: '联系电话',
  package_items: '选择套餐',
  images: '推广图片',
};

const RECRUITMENT_MAP_LABELS: Record<string, string> = {
  job_title: '岗位',
  salary: '工资',
  recruiter: '招方',
  job_description: '工作内容',
  contact_phone: '联系电话',
  package_items: '选择套餐',
};

const FREQ_OPTIONS = [
  { value: 'daily1', label: '每天 1 次' },
  { value: 'daily2', label: '每天 2 次' },
  { value: 'every2', label: '每 2 天 1 次' },
  { value: 'every3', label: '每 3 天 1 次' },
  { value: 'weekly', label: '每周 1 次' },
] as const;

type FreqKey = (typeof FREQ_OPTIONS)[number]['value'];

/** 与服务端 mp_android_tasks 拉取过滤规则一致（仅说明，逻辑在 PHP） */
const FREQ_PULL_HINTS: Record<FreqKey, string> = {
  daily1: '同一机器人同一信息：自然日内最多 1 次；次日可再拉。',
  daily2: '同一自然日最多 2 次，两次之间须间隔下方「间隔（小时）」；未到间隔不会下发任务。',
  every2: '距上次成功推广须满 2 个自然日（例：1 日执行后，2 日不可拉，3 日起可拉）。',
  every3: '距上次成功推广须满 3 个自然日。',
  weekly: '距上次成功推广须满 7 个自然日。',
};

type ForwardPlaceholderDef = {
  key: string;
  /** 与上方「金数据字段映射」中的业务名称对应 */
  mapsTo: string;
  desc: string;
};

/** 图文信息：转发模板可用占位符（花括号内英文，非 field_X） */
const GRAPHIC_FORWARD_PLACEHOLDERS: ForwardPlaceholderDef[] = [
  {
    key: 'promotion_text',
    mapsTo: '推广信息',
    desc: '用户提交的正文推广文案（由 Webhook 按映射字段解析）',
  },
  {
    key: 'contact_phone',
    mapsTo: '联系电话',
    desc: '用户填写的联系电话',
  },
  {
    key: 'display_text',
    mapsTo: '（系统汇总）',
    desc: '本条在后台列表中展示的完整文字，图文类型下与推广信息正文一致',
  },
];

/** 招聘信息：转发模板可用占位符 */
const RECRUITMENT_FORWARD_PLACEHOLDERS: ForwardPlaceholderDef[] = [
  {
    key: 'job_title',
    mapsTo: '岗位',
    desc: '招聘岗位名称',
  },
  {
    key: 'salary',
    mapsTo: '工资',
    desc: '薪资说明（用户填写原文）',
  },
  {
    key: 'recruiter',
    mapsTo: '招方',
    desc: '招聘方 / 用人单位名称',
  },
  {
    key: 'job_description',
    mapsTo: '工作内容',
    desc: '岗位职责或工作描述正文',
  },
  {
    key: 'contact_phone',
    mapsTo: '联系电话',
    desc: '咨询或报名联系电话',
  },
  {
    key: 'display_text',
    mapsTo: '（系统汇总）',
    desc: '岗位、工资、招方、工作内容、电话等拼接后的完整展示文案（与列表「推广信息」列一致）',
  },
];

const FORWARD_TEMPLATE_EXAMPLE: Record<MicroPromoInfoType, string> = {
  graphic: '{promotion_text}\n联系电话：{contact_phone}',
  recruitment: '【{job_title}】{salary}\n招方：{recruiter}\n{job_description}\n电话：{contact_phone}',
};

type Props = {
  areaId: number;
};

export function MicroPromotionParamsPanel({ areaId }: Props) {
  const [infoType, setInfoType] = useState<MicroPromoInfoType>('graphic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [fieldMap, setFieldMap] = useState<Record<string, string>>({});
  const [forwardTemplate, setForwardTemplate] = useState('');
  const [promotionTextMaxLen, setPromotionTextMaxLen] = useState(120);
  const [formCode, setFormCode] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [momentsEnabled, setMomentsEnabled] = useState(true);
  const [groupsEnabled, setGroupsEnabled] = useState(true);
  const [momentsFreq, setMomentsFreq] = useState<FreqKey>('daily1');
  const [groupsFreq, setGroupsFreq] = useState<FreqKey>('every2');
  const [weworkPushEnabled, setWeworkPushEnabled] = useState(false);
  const [weworkPushFreq, setWeworkPushFreq] = useState<FreqKey>('daily1');
  const [weworkWebhookUrl, setWeworkWebhookUrl] = useState('');
  const [weworkTimeRanges, setWeworkTimeRanges] = useState<WeworkPushTimeRangeRow[]>([
    { id: 'default', start: '07:00', end: '22:00' },
  ]);
  const [weworkMinIntervalMinutes, setWeworkMinIntervalMinutes] = useState(0);
  const [daily2Hours, setDaily2Hours] = useState(6);
  const [defaultImages, setDefaultImages] = useState<DefaultMomentsImageItem[]>([]);
  const [copied, setCopied] = useState(false);
  const forwardRef = useRef<HTMLTextAreaElement>(null);

  const mapLabels = infoType === 'graphic' ? GRAPHIC_MAP_LABELS : RECRUITMENT_MAP_LABELS;
  const forwardPlaceholders = infoType === 'graphic' ? GRAPHIC_FORWARD_PLACEHOLDERS : RECRUITMENT_FORWARD_PLACEHOLDERS;
  const showDaily2Interval =
    (momentsEnabled && momentsFreq === 'daily2')
    || (groupsEnabled && groupsFreq === 'daily2')
    || (weworkPushEnabled && weworkPushFreq === 'daily2');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        map?: Record<string, string>;
        forward_content_template?: string;
        forward_promotion_text_max_len?: number;
        jinsuju_form_code?: string;
        jinsuju_webhook_secret?: string;
        webhook_url?: string;
        promotion_moments_enabled?: boolean;
        promotion_groups_enabled?: boolean;
        promotion_moments_freq?: string;
        promotion_groups_freq?: string;
        promotion_daily2_interval_hours?: number;
        promotion_wework_push_enabled?: boolean;
        promotion_wework_push_freq?: string;
        wework_group_webhook_url?: string;
        wework_push_time_ranges?: Array<{ start: string; end: string }>;
        wework_push_min_interval_minutes?: number;
        default_moments_images?: DefaultMomentsImageItem[];
      }>('get_micro_promotion_area_settings', {
        method: 'GET',
        query: { area_id: areaId, info_type: infoType },
      });
      if (r.ok) {
        setFieldMap(r.map || {});
        setForwardTemplate(r.forward_content_template || '');
        setPromotionTextMaxLen(
          typeof r.forward_promotion_text_max_len === 'number' && r.forward_promotion_text_max_len > 0
            ? r.forward_promotion_text_max_len
            : 120,
        );
        setFormCode(r.jinsuju_form_code || '');
        setWebhookSecret(r.jinsuju_webhook_secret || '');
        setWebhookUrl(r.webhook_url || '');
        setMomentsEnabled(r.promotion_moments_enabled !== false);
        setGroupsEnabled(r.promotion_groups_enabled !== false);
        if (r.promotion_moments_freq) setMomentsFreq(r.promotion_moments_freq as FreqKey);
        if (r.promotion_groups_freq) setGroupsFreq(r.promotion_groups_freq as FreqKey);
        if (typeof r.promotion_daily2_interval_hours === 'number') {
          setDaily2Hours(r.promotion_daily2_interval_hours);
        }
        setWeworkPushEnabled(r.promotion_wework_push_enabled === true);
        if (r.promotion_wework_push_freq) setWeworkPushFreq(r.promotion_wework_push_freq as FreqKey);
        setWeworkWebhookUrl(r.wework_group_webhook_url || '');
        setWeworkTimeRanges(parseTimeRangesFromApi(r.wework_push_time_ranges));
        setWeworkMinIntervalMinutes(
          typeof r.wework_push_min_interval_minutes === 'number' ? r.wework_push_min_interval_minutes : 0,
        );
        setDefaultImages(Array.isArray(r.default_moments_images) ? r.default_moments_images : []);
      }
    } finally {
      setLoading(false);
    }
  }, [areaId, infoType]);

  useEffect(() => {
    void load();
  }, [load]);

  function validateBeforeSave(): string | null {
    if (momentsEnabled && (defaultImages.length < 1 || defaultImages.length > 9)) {
      return '启用发圈时须配置 1～9 张默认配图';
    }
    if (weworkPushEnabled) {
      const u = weworkWebhookUrl.trim();
      if (!u.startsWith('https://')) {
        return '启用企微 Webhook 推送时须填写以 https:// 开头的机器人地址';
      }
      const scheduleErr = validateWeworkPushSchedule(weworkTimeRanges, weworkMinIntervalMinutes);
      if (scheduleErr) {
        return scheduleErr;
      }
    }
    return null;
  }

  async function saveAll() {
    const v = validateBeforeSave();
    if (v) {
      setMsg(v);
      return;
    }
    setSaving(true);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('save_micro_promotion_area_settings', {
        method: 'POST',
        body: JSON.stringify({
          area_id: areaId,
          info_type: infoType,
          map: fieldMap,
          forward_content_template: forwardTemplate,
          forward_promotion_text_max_len: promotionTextMaxLen,
          jinsuju_form_code: formCode,
          jinsuju_webhook_secret: webhookSecret,
          promotion_moments_enabled: momentsEnabled,
          promotion_groups_enabled: groupsEnabled,
          promotion_moments_freq: momentsEnabled ? momentsFreq : undefined,
          promotion_groups_freq: groupsEnabled ? groupsFreq : undefined,
          promotion_wework_push_enabled: weworkPushEnabled,
          promotion_wework_push_freq: weworkPushEnabled ? weworkPushFreq : undefined,
          wework_group_webhook_url: weworkPushEnabled ? weworkWebhookUrl.trim() : '',
          wework_push_time_ranges: weworkPushEnabled
            ? weworkTimeRanges.map((r) => ({ start: r.start, end: r.end }))
            : [],
          wework_push_min_interval_minutes: weworkPushEnabled ? weworkMinIntervalMinutes : 0,
          promotion_daily2_interval_hours: showDaily2Interval ? daily2Hours : undefined,
          default_moments_image_paths: momentsEnabled ? defaultImages.map((x) => x.path) : [],
        }),
      });
      if (!r.ok) {
        setMsg(r.detail || r.err || '保存失败');
        return;
      }
      setMsg('已保存');
      await load();
    } finally {
      setSaving(false);
    }
  }

  function insertPlaceholder(key: string) {
    const token = `{${key}}`;
    const el = forwardRef.current;
    if (!el) {
      setForwardTemplate((t) => t + token);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    setForwardTemplate(el.value.slice(0, start) + token + el.value.slice(end));
  }

  const typeHint =
    infoType === 'graphic'
      ? '对接金数据「图文信息」表单（如 field_4 推广文案、field_34 图片）。'
      : '对接金数据「招聘信息」表单（岗位 field_2、工资 field_3、招方 field_27、工作内容 field_4 等）。';

  return (
    <div className="space-y-5">
      <SegmentedTabs
        ariaLabel="信息类型"
        value={infoType}
        onChange={setInfoType}
        items={[
          { id: 'graphic', label: '图文信息' },
          { id: 'recruitment', label: '招聘信息' },
        ]}
      />
      <p className="text-sm text-slate-600">{typeHint}</p>

      {loading ? (
        <Spinner label="加载参数" />
      ) : (
        <>
          <details className={detailsShellClass} open>
            <summary className={detailsSummaryClass}>
              <h2 className={detailsTitleClass}>金数据 Webhook</h2>
            </summary>
            <div className="space-y-3 p-5 text-sm text-slate-700">
              <label className="block">
                <span className={labelClass}>表单 form 代码（可选校验）</span>
                <input
                  className={inputClass}
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value.trim())}
                  placeholder={infoType === 'graphic' ? '如 CvnDG8' : '如 Wly5Tj'}
                  spellCheck={false}
                />
              </label>
              <label className="block">
                <span className={labelClass}>Webhook 密钥</span>
                <input
                  className={`${inputClass} font-mono text-xs`}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  spellCheck={false}
                />
              </label>
              <p className="text-xs text-slate-500">POST 地址（可复制到金数据「提交后推送」）：</p>
              <div className="break-all rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 font-mono text-xs text-slate-800">
                {webhookUrl || '请配置 site_url 后刷新'}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (webhookUrl) {
                      void navigator.clipboard.writeText(webhookUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }
                  }}
                >
                  复制 Webhook 地址
                </Button>
                {copied ? <span className="self-center text-xs text-emerald-700">已复制</span> : null}
              </div>
            </div>
          </details>

          <details className={detailsShellClass} open>
            <summary className={detailsSummaryClass}>
              <h2 className={detailsTitleClass}>推广任务 · 默认频次</h2>
            </summary>
            <div className="space-y-6 p-5">
              <div
                className="rounded-xl border border-sky-200/90 bg-sky-50/70 px-4 py-3 text-xs leading-relaxed text-sky-950"
                role="note"
              >
                <p className="font-semibold text-sky-900">推广频次说明</p>
                <p className="mt-1 text-sky-900/85">
                  频次与套餐天数决定推广期内<strong className="font-medium">总次数</strong>。企微推送还须落在配置的<strong className="font-medium">允许时段</strong>内，并满足<strong className="font-medium">最小间隔分钟</strong>与频次规则。
                </p>
              </div>
              <div className="rounded-xl border border-slate-200/90 bg-slate-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-800">启动：发圈</p>
                  <LabeledToggleSwitch
                    checked={momentsEnabled}
                    aria-label="启动：发圈"
                    onChange={setMomentsEnabled}
                  />
                </div>
                {momentsEnabled ? (
                  <p className="text-xs text-slate-500">朋友圈推广任务</p>
                ) : null}
                {momentsEnabled ? (
                  <div className="mt-3 space-y-4 border-t border-slate-200/80 pt-4">
                    <label className="flex max-w-md flex-col gap-1.5">
                      <span className={labelClass}>发圈默认频次</span>
                      <select
                        className={inputClass}
                        value={momentsFreq}
                        onChange={(e) => setMomentsFreq(e.target.value as FreqKey)}
                      >
                        {FREQ_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-slate-500">{FREQ_PULL_HINTS[momentsFreq]}</span>
                    </label>
                    <div>
                      <p className={labelClass}>发圈默认配图</p>
                      <MicroPromotionDefaultMomentsImages
                        areaId={areaId}
                        infoType={infoType}
                        items={defaultImages}
                        onChange={setDefaultImages}
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-slate-200/90 bg-slate-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-800">启动：群发</p>
                  <LabeledToggleSwitch
                    checked={groupsEnabled}
                    aria-label="启动：群发"
                    onChange={setGroupsEnabled}
                  />
                </div>
                <p className="text-xs text-amber-800/90">仅文字部分（图片不转发）</p>
                {groupsEnabled ? (
                  <label className="mt-2 flex max-w-md flex-col gap-1.5 border-t border-slate-200/80 pt-4">
                    <span className={labelClass}>群发默认频次</span>
                    <select
                      className={inputClass}
                      value={groupsFreq}
                      onChange={(e) => setGroupsFreq(e.target.value as FreqKey)}
                    >
                      {FREQ_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-slate-500">{FREQ_PULL_HINTS[groupsFreq]}</span>
                  </label>
                ) : null}
              </div>

              <div className="rounded-xl border border-slate-200/90 bg-slate-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-800">启动：Webhook 推送</p>
                  <LabeledToggleSwitch
                    checked={weworkPushEnabled}
                    aria-label="启动：Webhook 推送"
                    onChange={setWeworkPushEnabled}
                  />
                </div>
                <p className="text-xs text-slate-500">向企业微信内部群机器人推送推广文案（使用下方转发模板渲染正文）</p>
                {weworkPushEnabled ? (
                  <div className="mt-3 space-y-4 border-t border-slate-200/80 pt-4">
                    <label className="flex max-w-md flex-col gap-1.5">
                      <span className={labelClass}>推送默认频次</span>
                      <select
                        className={inputClass}
                        value={weworkPushFreq}
                        onChange={(e) => setWeworkPushFreq(e.target.value as FreqKey)}
                      >
                        {FREQ_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-slate-500">{FREQ_PULL_HINTS[weworkPushFreq]}</span>
                    </label>
                    <label className="flex max-w-2xl flex-col gap-1.5">
                      <span className={labelClass}>企微内部群 Webhook URL</span>
                      <input
                        type="url"
                        className={`${inputClass} font-mono text-xs`}
                        value={weworkWebhookUrl}
                        onChange={(e) => setWeworkWebhookUrl(e.target.value)}
                        placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=..."
                        spellCheck={false}
                        autoComplete="off"
                      />
                      <span className="text-xs text-slate-500">
                        在企业微信群 → 群机器人 → 添加机器人 → 复制 Webhook 地址。
                      </span>
                    </label>
                    <WeworkPushTimeRangesEditor
                      ranges={weworkTimeRanges}
                      minIntervalMinutes={weworkMinIntervalMinutes}
                      onRangesChange={setWeworkTimeRanges}
                      onMinIntervalChange={setWeworkMinIntervalMinutes}
                    />
                    <p className="text-xs text-slate-500">
                      新条目入库后会尝试推送 1 条；定时任务每次最多推送 1 条，并按「最小推送间隔」控制下一条。仅在允许时段内且满足频次时才会发送。
                    </p>
                    <Link
                      to={`/micro-promotion-wework-push-logs?area_id=${areaId}`}
                      className="inline-flex text-xs font-medium text-teal-700 hover:text-teal-900 hover:underline"
                    >
                      查看企微推送日志 →
                    </Link>
                  </div>
                ) : null}
              </div>

              {showDaily2Interval ? (
                <label className="flex max-w-xs flex-col gap-1.5">
                  <span className={labelClass}>每天 2 次 · 间隔（小时）</span>
                  <input
                    type="number"
                    min={1}
                    max={24}
                    className={inputClass}
                    value={daily2Hours}
                    onChange={(e) => setDaily2Hours(Number(e.target.value) || 6)}
                  />
                  <span className="text-xs text-slate-500">
                    发圈、群发或 Webhook 推送任一为「每天 2 次」时生效；服务端按此间隔控制第二次执行。
                  </span>
                </label>
              ) : null}
            </div>
          </details>

          <details className={detailsShellClass} open>
            <summary className={detailsSummaryClass}>
              <h2 className={detailsTitleClass}>金数据字段映射</h2>
            </summary>
            <div className="space-y-3 p-5">
              <p className="text-xs text-slate-500">
                将业务含义映射到金数据 entry 字段名；微信昵称使用固定字段{' '}
                <code className="rounded bg-slate-100 px-1">x_field_weixin_nickname</code>，无需配置。
              </p>
              {(Object.keys(mapLabels) as string[]).map((key) => (
                <label key={key} className="block text-xs">
                  <span className="mb-1 block font-medium text-slate-800">{mapLabels[key]}</span>
                  <input
                    type="text"
                    className={`${inputClass} font-mono text-sm`}
                    value={fieldMap[key] ?? ''}
                    onChange={(e) => setFieldMap((m) => ({ ...m, [key]: e.target.value }))}
                    spellCheck={false}
                  />
                </label>
              ))}
              <label className="block text-xs">
                <span className="mb-1 block font-medium text-slate-800">转发内容模板（Android 纯文字）</span>
                <div className="mb-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-[11px] leading-relaxed text-slate-600">
                  <p>
                    Android 拉取任务时用于发送的<strong className="font-medium text-slate-800">纯文字</strong>
                    （不含图片；发圈配图由默认配图或本条附图单独下发，群发仅发文字）。
                  </p>
                  <p>
                    <strong className="font-medium text-slate-800">请勿与上方字段映射混淆：</strong>
                    映射里填的是金数据字段名（如 <code className="rounded bg-white px-1 font-mono text-[10px]">field_4</code>
                    ）；模板里须写下方<strong>占位符</strong>（花括号英文），保存后由系统按本条入库数据替换。
                  </p>
                  <p className="font-medium text-slate-700">占位符含义（{infoType === 'graphic' ? '图文信息' : '招聘信息'}）：</p>
                  <ul className="space-y-1.5 border-l-2 border-teal-500/40 pl-3">
                    {forwardPlaceholders.map((p) => (
                      <li key={p.key}>
                        <code className="rounded bg-white px-1 py-0.5 font-mono text-[10px] text-slate-800">{`{${p.key}}`}</code>
                        <span className="text-slate-500"> — </span>
                        <span className="text-slate-700">{p.desc}</span>
                        <span className="block text-slate-500">对应映射项：{p.mapsTo}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-500">留空时：转发正文与系统汇总的 <code className="rounded bg-white px-1 font-mono text-[10px]">{'{display_text}'}</code> 一致。</p>
                  <div className="mt-3 flex max-w-xs flex-col gap-1.5 border-t border-slate-200/80 pt-3">
                    <label className="text-[11px] font-medium text-slate-700" htmlFor="mp-promo-text-max-len">
                      <code className="rounded bg-white px-1 font-mono text-[10px]">{'{promotion_text}'}</code> 最大字数
                    </label>
                    <input
                      id="mp-promo-text-max-len"
                      type="number"
                      min={1}
                      max={500}
                      className={inputClass}
                      value={promotionTextMaxLen}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        setPromotionTextMaxLen(Number.isFinite(n) && n > 0 ? Math.min(500, n) : 120);
                      }}
                    />
                    <span className="text-[11px] text-slate-500">
                      默认 120。模板替换时若正文超出该长度，将自动截取前 N 字，避免 Android 纯文字转发过长。
                    </span>
                  </div>
                </div>
                <textarea
                  ref={forwardRef}
                  className={`${inputClass} min-h-[120px] font-mono text-sm`}
                  rows={5}
                  value={forwardTemplate}
                  onChange={(e) => setForwardTemplate(e.target.value)}
                  placeholder={`例如：\n${FORWARD_TEMPLATE_EXAMPLE[infoType]}`}
                />
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-medium text-slate-600">插入占位符：</span>
                  {forwardPlaceholders.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      title={p.desc}
                      className="rounded border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] text-slate-800 hover:border-teal-300 hover:bg-teal-50"
                      onClick={() => insertPlaceholder(p.key)}
                    >
                      {`{${p.key}}`}
                    </button>
                  ))}
                </div>
              </label>
            </div>
          </details>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="primary" disabled={saving} onClick={() => void saveAll()}>
              {saving ? '保存中…' : '保存配置'}
            </Button>
            <Button type="button" variant="secondary" disabled={loading} onClick={() => void load()}>
              重新加载
            </Button>
            {msg ? (
              <span className={`text-xs ${msg.includes('失败') || msg.includes('须') ? 'text-rose-600' : 'text-emerald-700'}`}>
                {msg}
              </span>
            ) : null}
          </div>
          <p className="text-xs text-slate-500">
            套餐名称与天数对照仍在服务器 <code className="rounded bg-slate-100 px-1">api/config.php</code> 的{' '}
            <code className="rounded bg-slate-100 px-1">package_day_map</code> 中维护。
          </p>
        </>
      )}
    </div>
  );
}
